import {
  phoneIsValid,
  userEmailOrMobileIsValid,
  userFiledsAreValid,
  userNameIsValid
} from './utils';
import {
  applyValueInheritance,
  prepareDocumentsForCaseConditions
} from './formUtils';
import { FORMELEMENTS_TEXTINPUT } from '../FormBuilder/FormElements';
import {
  defineCurrentRecipientRoles,
  isWidgetForCurrentRecipient
} from '../../redux/utils';
import { CaseConditionalLogic } from '@eqx/paperfly-conditional-lib';

const getUserFieldLinkedData = (
  props,
  recipientId,
  ques_field_name,
  embedding_field_name
) => {
  const valueInheritance =
    props.qesValueInheritance?.[recipientId]?.[ques_field_name] ||
    props.embeddingFieldsInheritance[embedding_field_name];

  return (
    valueInheritance &&
    applyValueInheritance(
      {
        element: FORMELEMENTS_TEXTINPUT,
        valueInheritance
      },
      props.preparedFormsData,
      props.preparedFormsStructures
    )
  );
};

/**
 * Portal "Assign field" may store {{DOCUMENT__docId__fieldId}} in recipient
 * name/email/phone. Resolve via the same placeholdersStr path as linked fields.
 */
const resolveRecipientContactDefault = (raw, props) => {
  if (typeof raw !== 'string' || !raw.includes('{{DOCUMENT__')) {
    return raw;
  }

  return (
    applyValueInheritance(
      {
        element: FORMELEMENTS_TEXTINPUT,
        valueInheritance: { placeholdersStr: raw }
      },
      props.preparedFormsData,
      props.preparedFormsStructures
    ) ?? ''
  );
};

const getUsersDataProp = (
  props,
  index,
  prop,
  defaultProp,
  linkedPropName,
  qesLinkedPropNmame
) => {
  return props.usersData[index][prop] !== undefined
    ? props.usersData[index][prop]
    : getUserFieldLinkedData(
        props,
        props.usersData[index].id,
        qesLinkedPropNmame,
        linkedPropName + (index > 0 ? '_' + index : '')
      ) ||
        resolveRecipientContactDefault(
          props.usersData[index][defaultProp],
          props
        );
};

const userName = (props, index) =>
  getUsersDataProp(props, index, 'userName', 'recipientName', 'name_field');

const qesFirstName = (props, index) =>
  getUsersDataProp(
    props,
    index,
    'qesFirstName',
    'qesFirstName',
    '',
    'firstName'
  );

const qesLastName = (props, index) =>
  getUsersDataProp(props, index, 'qesLastName', 'qesLastName', '', 'lastName');

const userEmail = (props, index) =>
  getUsersDataProp(
    props,
    index,
    'userEmail',
    'recipientEmail',
    'email_field',
    'email'
  );

const userPhone = (props, index) =>
  getUsersDataProp(
    props,
    index,
    'userPhone',
    'recipientPhone',
    'phone_field',
    'phone'
  );

const userFieldValue = (props, recipientId, field) =>
  field.value !== undefined
    ? field.value
    : getUserFieldLinkedData(props, recipientId, '', field.id);

const userFields = (props, index) =>
  props.usersData[index].userFields
    ? props.usersData[index].userFields.map(f => ({
        ...f,
        value: userFieldValue(props, props.usersData[index].id, f)
      }))
    : [];

export const isUserEnabled = (props, user) => {
  const condition = props.recipientConditions?.find(
    c => c.id === user.conditionId
  );

  if (condition) {
    return CaseConditionalLogic.checkCondition(
      condition,
      prepareDocumentsForCaseConditions(
        props.requestedDocuments,
        props.preparedFormsData
      )
    );
  } else return true;
};

const checkRecipientHasSpecialSignature = (
  props,
  recipientId,
  specialSignaturesListKey
) =>
  props.requestedDocuments.some(d =>
    d[specialSignaturesListKey]?.some(s => {
      const currentRecipientRoles = defineCurrentRecipientRoles(
        recipientId,
        d.recipientInfos
      );
      return isWidgetForCurrentRecipient(
        currentRecipientRoles,
        s.recipientRole
      );
    })
  );

export const usersDataOk = user =>
  !user.enabled ||
  (userEmailOrMobileIsValid(user.userEmail) &&
    userFiledsAreValid(user.userFields) &&
    (!user.needAes || phoneIsValid(user.userPhone)) &&
    (user.needQes
      ? userNameIsValid(user.qesFirstName) && userNameIsValid(user.qesLastName)
      : userNameIsValid(user.userName)));

const CUSTOM_RECIPIENT_FIELDS = [
  'userName',
  'qesFirstName',
  'qesLastName',
  'userEmail',
  'userPhone'
];

/**
 * Wire / display recipient name (R-C5).
 * When needQes, qesFirstName+qesLastName are source of truth — not inherited userName.
 */
export const resolveRecipientName = ({
  needQes,
  userName: plainUserName,
  qesFirstName: first,
  qesLastName: last
} = {}) =>
  needQes ? (first && last ? `${first} ${last}` : undefined) : plainUserName;

/**
 * Hard-override fields on already-built usersData (e.g. QES registration).
 */
export const applyCustomRecipientsData = (
  usersData,
  customRecipientsDataSource
) => {
  if (!customRecipientsDataSource?.length) {
    return usersData;
  }

  return usersData.map((user, index) => {
    const custom = customRecipientsDataSource[index];
    if (!custom) {
      return user;
    }

    const next = { ...user };
    CUSTOM_RECIPIENT_FIELDS.forEach(key => {
      if (custom[key] !== undefined) {
        next[key] = custom[key];
      }
    });

    next.userName = resolveRecipientName(next);
    next.ok = usersDataOk(next);
    return next;
  });
};

export const buildUsersData = props =>
  props.usersData.map((userData, index) => {
    const user = {
      userName: userName(props, index),
      qesFirstName: qesFirstName(props, index),
      qesLastName: qesLastName(props, index),
      rawUserName: userData?.userName,
      userEmail: userEmail(props, index),
      rawUserEmail: userData?.userEmail,
      userPhone: userPhone(props, index),
      userFields: userFields(props, index),
      needAes: checkRecipientHasSpecialSignature(
        props,
        userData.id,
        'allAdvancedSignaturesList'
      ),
      needQes: checkRecipientHasSpecialSignature(
        props,
        userData.id,
        'allQualifiedSignaturesList'
      ),
      enabled: isUserEnabled(props, userData)
    };

    user.userName = resolveRecipientName(user);
    return {
      ...userData,
      ...user,
      ok: usersDataOk(user)
    };
  });

export const usersDataAllOk = data => data.every(user => user.ok);

/**
 * Anonymized single-user summary for analytics (no personal data).
 * @param {object} user
 * @returns {string}
 */
export const getUserDataInfo = user => {
  if (!user) {
    return 'missing';
  }

  const idPrefix = user.id ? `${String(user.id).substring(0, 4)}...` : '-';
  const fields = Array.isArray(user.userFields) ? user.userFields : [];
  const fieldsFilled = fields.filter(
    f => f?.value !== undefined && f?.value !== null && f?.value !== ''
  ).length;

  return [
    `id:${idPrefix}`,
    `enabled:${Boolean(user.enabled)}`,
    `ok:${Boolean(user.ok)}`,
    `needQes:${Boolean(user.needQes)}`,
    `needAes:${Boolean(user.needAes)}`,
    `hasUserName:${Boolean(user.userName)}`,
    `hasQesFirstName:${Boolean(user.qesFirstName)}`,
    `hasQesLastName:${Boolean(user.qesLastName)}`,
    `hasEmail:${Boolean(user.userEmail)}`,
    `hasPhone:${Boolean(user.userPhone)}`,
    `fields:${fieldsFilled}/${fields.length}`,
    user.role ? `role:${user.role}` : null
  ]
    .filter(Boolean)
    .map(line => `  ${line}`)
    .join('\n');
};

/**
 * Anonymized preparedUsersData summary for analytics (like getRequestDocumentsInfo).
 * @param {Array} preparedUsersData
 * @returns {string}
 */
export const getPreparedUsersDataInfo = preparedUsersData => {
  let info = `\n`;
  (preparedUsersData || []).forEach((user, index) => {
    info += `\n User ${index + 1}:\n${getUserDataInfo(user)}`;
  });
  return info + '\n\n';
};
